import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../Axios';
import { toggleProjectModal } from 'reducers/UiReducer';

const getAxiosConfig = (token) => ({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  },
});

const getErrorMessage = (error) =>
  error.response && error.response.data.message ? error.response.data.message : error.message;

const ProjectReducer = createSlice({
  name: 'project',
  initialState: {
    projectLoading: false,
    projectList: [],
    projectError: null,
    projectSuccessMessage: '',
  },
  reducers: {
    createProjectRequest(state) {
      state.projectLoading = true;
      state.projectError = null;
      state.projectSuccessMessage = '';
    },
    createProjectSuccess(state, action) {
      state.projectLoading = false;
      state.projectSuccessMessage = action.payload || 'Project saved successfully';
    },
    createProjectFail(state, action) {
      state.projectLoading = false;
      state.projectError = action.payload;
    },
    updateProjectRequest(state) {
      state.projectLoading = true;
      state.projectError = null;
      state.projectSuccessMessage = '';
    },
    updateProjectSuccess(state, action) {
      state.projectLoading = false;
      state.projectSuccessMessage = action.payload || 'Project updated successfully';
    },
    updateProjectFail(state, action) {
      state.projectLoading = false;
      state.projectError = action.payload;
    },
    getProjectListRequest(state) {
      state.projectLoading = true;
    },
    getProjectListSuccess(state, action) {
      state.projectLoading = false;
      state.projectList = action.payload;
    },
    getProjectListFail(state, action) {
      state.projectLoading = false;
      state.projectError = action.payload;
    },
    deleteProjectRequest(state) {
      state.projectLoading = true;
    },
    deleteProjectSuccess(state) {
      state.projectLoading = false;
    },
    deleteProjectFail(state, action) {
      state.projectLoading = false;
      state.projectError = action.payload;
    },
    createProjectEmployeeRequest(state) {
      state.projectLoading = true;
      state.projectError = null;
      state.projectSuccessMessage = '';
    },
    createProjectEmployeeSuccess(state, action) {
      state.projectLoading = false;
      state.projectSuccessMessage = action.payload || 'Project allocated successfully';
    },
    createProjectEmployeeFail(state, action) {
      state.projectLoading = false;
      state.projectError = action.payload;
    },
  },
});

export const {
  createProjectRequest,
  createProjectSuccess,
  createProjectFail,
  updateProjectRequest,
  updateProjectSuccess,
  updateProjectFail,
  getProjectListRequest,
  getProjectListSuccess,
  getProjectListFail,
  deleteProjectRequest,
  deleteProjectSuccess,
  deleteProjectFail,
  createProjectEmployeeRequest,
  createProjectEmployeeSuccess,
  createProjectEmployeeFail,
} = ProjectReducer.actions;

export const createProjectDispatch = (projectData, token) => async (dispatch) => {
  try {
    dispatch(createProjectRequest());
    const { data } = await axiosInstance.post('/Project/CreateProject', projectData, getAxiosConfig(token));

    if (data.status === 1) {
      dispatch(createProjectSuccess(data.message || 'Project created successfully'));
      dispatch(getProjectListDispatch({ Offset: 0, RecordCount: 20, ProjectName: '' }, token));
      dispatch(toggleProjectModal(false));
    } else {
      dispatch(createProjectFail(data.message || 'Unable to create project'));
    }

    return data;
  } catch (error) {
    dispatch(createProjectFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const updateProjectDispatch = (projectData, token) => async (dispatch) => {
  try {
    dispatch(updateProjectRequest());
    const { data } = await axiosInstance.post('/Project/UpdateProject', projectData, getAxiosConfig(token));

    if (data.status === 1) {
      dispatch(updateProjectSuccess(data.message || 'Project updated successfully'));
      dispatch(getProjectListDispatch({ Offset: 0, RecordCount: 20, ProjectName: '' }, token));
      dispatch(toggleProjectModal(false));
    } else {
      dispatch(updateProjectFail(data.message || 'Unable to update project'));
    }

    return data;
  } catch (error) {
    dispatch(updateProjectFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getProjectListDispatch = (form, token) => async (dispatch) => {
  try {
    dispatch(getProjectListRequest());
    const projectDto = form?.projectDto || {};
    const projectName =
      form?.ProjectName ?? form?.projectName ?? projectDto.ProjectName ?? projectDto.projectName ?? '';
    const offset = form?.Offset ?? form?.offset ?? projectDto.offset ?? 0;
    const recordCount =
      form?.RecordCount ?? form?.recordCount ?? projectDto.recordCount ?? projectDto.pageSize ?? 20;

    const { data } = await axiosInstance.get('/Project/GetProjectList', {
      ...getAxiosConfig(token),
      params: {
        Offset: offset,
        RecordCount: recordCount,
        ProjectName: projectName,
      },
    });

    dispatch(getProjectListSuccess(data));
  } catch (error) {
    dispatch(getProjectListFail(getErrorMessage(error)));
  }
};

export const deleteProjectDispatch = (projectId, token) => async (dispatch) => {
  try {
    dispatch(deleteProjectRequest());
    await axiosInstance.delete(`/Project/DeleteProjectById/${projectId}`, getAxiosConfig(token));
    dispatch(deleteProjectSuccess());
  } catch (error) {
    dispatch(deleteProjectFail(getErrorMessage(error)));
  }
};

export const createProjectEmployeeDispatch = (projectEmployeeData, token) => async (dispatch) => {
  try {
    dispatch(createProjectEmployeeRequest());
    const { data } = await axiosInstance.post(
      '/ProjectEmployee/CreateProjectEmployee',
      projectEmployeeData,
      getAxiosConfig(token)
    );

    if (data.status === 1) {
      dispatch(createProjectEmployeeSuccess(data.message || 'Project allocated successfully'));
    } else {
      dispatch(createProjectEmployeeFail(data.message || 'Unable to allocate project'));
    }

    return data;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch(createProjectEmployeeFail(message));
    return {
      status: 0,
      message,
    };
  }
};

export default ProjectReducer;
